const CIRRA_TOOL_RETRY_CLASS = Object.freeze({
  none: 'none',
  network: 'network',
  provider: 'provider',
  rateLimit: 'rate_limit',
  validation: 'validation'
});

const CIRRA_TOOL_PLANE = Object.freeze({
  desktopRelay: 'desktop_relay',
  knowledge: 'knowledge',
  sharedVault: 'shared_vault',
  mcp: 'mcp',
  web: 'web',
  subagent: 'subagent'
});

const CIRRA_WEB_SEARCH_FUNCTION_TOOL_NAME = 'web_search';
const CIRRA_WEB_SEARCH_TOOL_TYPES = new Set(['web_search_preview', 'web_search_preview_2025_03_11']);
const CIRRA_SUBAGENT_TOOL_NAME = 'create_subagent';
const CIRRA_AWAIT_SUBAGENTS_TOOL_NAME = 'await_subagents';

const DESKTOP_RELAY_TOOL_NAME_LIST = Object.freeze(['exec_command', 'shell_run', 'search_ripgrep', 'files_search', 'files_list', 'files_read', 'files_write', 'files_mkdir', 'files_stat', 'files_move', 'files_delete', 'files_preview', 'patch_apply', 'desktop_get_local_settings', 'desktop_update_local_settings']);

const CIRRA_DESKTOP_RELAY_TOOL_NAMES = new Set(DESKTOP_RELAY_TOOL_NAME_LIST);

const KNOWLEDGE_TOOL_NAME_LIST = Object.freeze(['knowledge_list', 'knowledge_read', 'knowledge_write', 'knowledge_stat', 'knowledge_move', 'knowledge_delete', 'knowledge_search_query']);

const CIRRA_KNOWLEDGE_TOOL_NAMES = new Set(KNOWLEDGE_TOOL_NAME_LIST);
const CIRRA_SHARED_VAULT_TOOL_NAMES = new Set(KNOWLEDGE_TOOL_NAME_LIST);

const LOCAL_DISCOVERY_TOOL_NAME_LIST = Object.freeze(['search_ripgrep', 'files_search', 'files_list', 'files_read', 'files_preview', 'exec_command', 'shell_run']);
const CIRRA_LOCAL_DISCOVERY_TOOL_NAMES = new Set(LOCAL_DISCOVERY_TOOL_NAME_LIST);
const CIRRA_LOCAL_BROAD_DISCOVERY_TOOL_NAMES = new Set(LOCAL_DISCOVERY_TOOL_NAME_LIST);

const READ_ONLY_LOCAL_TOOL_NAME_LIST = Object.freeze(['search_ripgrep', 'files_search', 'files_list', 'files_read', 'files_preview', 'files_stat']);
const CIRRA_READ_ONLY_LOCAL_TOOL_NAMES = new Set(READ_ONLY_LOCAL_TOOL_NAME_LIST);
const CIRRA_DYNAMIC_SHELL_TOOL_NAMES = new Set(['exec_command', 'shell_run']);

const MCP_READ_TOOL_NAME_LIST = Object.freeze(['cirra_mcp_apps_search', 'cirra_mcp_apps_get', 'cirra_mcp_actions_search', 'cirra_mcp_lookup_resolve', 'cirra_mcp_action_schema', 'cirra_mcp_result_query']);

const SHARED_READ_TOOL_NAME_LIST = Object.freeze(['knowledge_list', 'knowledge_read', 'knowledge_stat', 'knowledge_search_query']);

const WEB_READ_TOOL_NAME_LIST = Object.freeze([CIRRA_WEB_SEARCH_FUNCTION_TOOL_NAME, 'read_web_page']);

const CIRRA_API_PARALLEL_SAFE_TOOL_NAMES = new Set([...SHARED_READ_TOOL_NAME_LIST, ...MCP_READ_TOOL_NAME_LIST, ...WEB_READ_TOOL_NAME_LIST, ...READ_ONLY_LOCAL_TOOL_NAME_LIST]);

const CIRRA_API_IDEMPOTENT_TOOL_NAMES = new Set([...SHARED_READ_TOOL_NAME_LIST, ...MCP_READ_TOOL_NAME_LIST, CIRRA_AWAIT_SUBAGENTS_TOOL_NAME, ...WEB_READ_TOOL_NAME_LIST, ...READ_ONLY_LOCAL_TOOL_NAME_LIST]);

const CIRRA_API_PROVIDER_RETRY_TOOL_NAMES = new Set([...MCP_READ_TOOL_NAME_LIST, ...WEB_READ_TOOL_NAME_LIST]);

const CIRRA_API_NETWORK_RETRY_TOOL_NAMES = new Set([...SHARED_READ_TOOL_NAME_LIST, ...READ_ONLY_LOCAL_TOOL_NAME_LIST]);

const DESKTOP_READ_TOOL_NAME_LIST = Object.freeze(['files_list', 'files_read', 'files_preview', 'files_stat', 'files_search', 'search_ripgrep']);
const CIRRA_DESKTOP_PARALLEL_SAFE_TOOL_NAMES = new Set(DESKTOP_READ_TOOL_NAME_LIST);
const CIRRA_DESKTOP_IDEMPOTENT_TOOL_NAMES = new Set([...DESKTOP_READ_TOOL_NAME_LIST, CIRRA_WEB_SEARCH_FUNCTION_TOOL_NAME]);
const CIRRA_DESKTOP_NETWORK_RETRY_TOOL_NAMES = new Set(DESKTOP_READ_TOOL_NAME_LIST);
const CIRRA_DESKTOP_PROVIDER_RETRY_TOOL_NAMES = new Set([CIRRA_WEB_SEARCH_FUNCTION_TOOL_NAME]);

const CIRRA_TOOL_PLANES = Object.freeze({
  ...Object.fromEntries([...CIRRA_DESKTOP_RELAY_TOOL_NAMES].map((toolName) => [toolName, CIRRA_TOOL_PLANE.desktopRelay])),
  ...Object.fromEntries(KNOWLEDGE_TOOL_NAME_LIST.map((toolName) => [toolName, CIRRA_TOOL_PLANE.knowledge])),
  ...Object.fromEntries(MCP_READ_TOOL_NAME_LIST.map((toolName) => [toolName, CIRRA_TOOL_PLANE.mcp])),
  ...Object.fromEntries(WEB_READ_TOOL_NAME_LIST.map((toolName) => [toolName, CIRRA_TOOL_PLANE.web])),
  [CIRRA_SUBAGENT_TOOL_NAME]: CIRRA_TOOL_PLANE.subagent,
  [CIRRA_AWAIT_SUBAGENTS_TOOL_NAME]: CIRRA_TOOL_PLANE.subagent
});

const toSafeToolName = (toolName) => {
  return String(toolName || '').trim();
};

const isCirraDesktopRelayToolName = (toolName) => {
  return CIRRA_DESKTOP_RELAY_TOOL_NAMES.has(toSafeToolName(toolName));
};

const resolveCirraToolPlane = (toolName) => {
  return CIRRA_TOOL_PLANES[toSafeToolName(toolName)] || null;
};

export { CIRRA_TOOL_RETRY_CLASS, CIRRA_TOOL_PLANE, CIRRA_TOOL_PLANES, CIRRA_WEB_SEARCH_FUNCTION_TOOL_NAME, CIRRA_WEB_SEARCH_TOOL_TYPES, CIRRA_SUBAGENT_TOOL_NAME, CIRRA_AWAIT_SUBAGENTS_TOOL_NAME, CIRRA_DESKTOP_RELAY_TOOL_NAMES, CIRRA_KNOWLEDGE_TOOL_NAMES, CIRRA_SHARED_VAULT_TOOL_NAMES, CIRRA_LOCAL_DISCOVERY_TOOL_NAMES, CIRRA_LOCAL_BROAD_DISCOVERY_TOOL_NAMES, CIRRA_READ_ONLY_LOCAL_TOOL_NAMES, CIRRA_DYNAMIC_SHELL_TOOL_NAMES, CIRRA_API_PARALLEL_SAFE_TOOL_NAMES, CIRRA_API_IDEMPOTENT_TOOL_NAMES, CIRRA_API_PROVIDER_RETRY_TOOL_NAMES, CIRRA_API_NETWORK_RETRY_TOOL_NAMES, CIRRA_DESKTOP_PARALLEL_SAFE_TOOL_NAMES, CIRRA_DESKTOP_IDEMPOTENT_TOOL_NAMES, CIRRA_DESKTOP_NETWORK_RETRY_TOOL_NAMES, CIRRA_DESKTOP_PROVIDER_RETRY_TOOL_NAMES, isCirraDesktopRelayToolName, resolveCirraToolPlane };
